<template>
  <div
    class="wager-status-filters"
    role="tablist"
    :aria-label="$t('wagers.filterByStatus')"
  >
    <button
      v-for="option in options"
      :key="option.value"
      type="button"
      role="tab"
      class="wager-status-filter"
      :class="{ active: modelValue === option.value }"
      :aria-selected="modelValue === option.value"
      @click="$emit('update:modelValue', option.value)"
    >
      <span class="filter-text">{{ option.label }} {{ option.count }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

export type WagerStatusFilterValue = 'all' | 'open' | 'won' | 'lost'

export interface WagerStatusCounts {
  all: number
  open: number
  won: number
  lost: number
}

const props = defineProps<{
  modelValue: WagerStatusFilterValue
  counts: WagerStatusCounts
}>()

defineEmits<{
  (e: 'update:modelValue', value: WagerStatusFilterValue): void
}>()

const { t } = useI18n()

const options = computed(() => [
  { value: 'all' as const, label: t('wagers.filterAll'), count: props.counts.all },
  { value: 'open' as const, label: t('wagers.open'), count: props.counts.open },
  { value: 'won' as const, label: t('wagers.won'), count: props.counts.won },
  { value: 'lost' as const, label: t('wagers.lost'), count: props.counts.lost },
])
</script>

<style scoped>
.wager-status-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.wager-status-filter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid var(--wagers-filter-chip-border);
  background: var(--wagers-filter-chip-bg);
  color: var(--wagers-filter-chip-text);
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
}

.wager-status-filter:hover:not(.active) {
  border-color: color-mix(in srgb, var(--colour-orange-button) 45%, var(--colour-surface-cont));
}

.wager-status-filter.active {
  background: var(--colour-orange-button);
  border-color: var(--colour-orange-button);
  color: var(--text-button, #fff);
}

.filter-text {
  font-weight: 500;
  white-space: nowrap;
}

.wager-status-filter.active .filter-text {
  font-weight: 600;
}

@media (max-width: 768px) {
  .wager-status-filters {
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-end;
    gap: 4px;
    width: auto;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .wager-status-filters::-webkit-scrollbar {
    display: none;
  }

  .wager-status-filter {
    font-size: 11px;
    min-height: 28px;
    padding: 4px 8px;
  }
}
</style>
